import type { Book, SearchParams, SearchResponse } from '../types/library';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const LIBRARY_API_BASE = process.env.REACT_APP_LIBRARY_API_BASE || '/api/mock/library';

const getApiUrl = (endpoint: string): string => {
  return `${BACKEND_URL}${LIBRARY_API_BASE}${endpoint}`;
};

const buildQueryString = (params: SearchParams): string => {
  const query = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  
  return query.toString();
};

export const searchBooks = async (params: SearchParams): Promise<SearchResponse> => {
  const queryString = buildQueryString(params);
  const url = getApiUrl(`/books${queryString ? `?${queryString}` : ''}`);
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to search books: ${response.statusText}`);
  }
  
  return response.json();
};

export const getBook = async (id: string): Promise<Book> => {
  const url = getApiUrl(`/books/${id}`);
  
  const response = await fetch(url);
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Book not found');
    }
    throw new Error(`Failed to fetch book: ${response.statusText}`);
  }
  
  return response.json();
};

// Google Sheets Library Search
export interface GoogleSheetsBook {
  category?: string;
  genre?: string;
  author?: string;
  referenceNo?: string;
  copies?: string;
  title?: string;
  publisher?: string;
  publicationPlace?: string;
  coverUrl?: string;
  isbn?: string;
  isbn13?: string;
  isbn10?: string;
  description?: string;
  publicationYear?: string;
  subjects?: string[];
  language?: string;
  editionCount?: number;
  numberOfPages?: number;
  oclc?: string;
  lccn?: string;
}

export interface GoogleSheetsSearchResponse {
  results: GoogleSheetsBook[];
  total: number;
  query?: string;
}

export const searchGoogleSheetsLibrary = async (query?: string): Promise<GoogleSheetsSearchResponse> => {
  const url = `${BACKEND_URL}/api/library-search${query ? `?q=${encodeURIComponent(query)}` : ''}`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to search library: ${response.statusText}`);
  }
  
  return response.json();
};

export const getRandomLibrarySample = async (count: number = 12): Promise<GoogleSheetsSearchResponse> => {
  const url = `${BACKEND_URL}/api/library-random-sample?count=${count}`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch random sample: ${response.statusText}`);
  }
  
  return response.json();
};
